import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, Observable, of, ReplaySubject, tap, throwError,  } from 'rxjs';
import { HttpHeaders } from '@angular/common/http';
import { AuthService } from './auth.service';
import { ApiUrl } from '../constants/api-url';
import { getRoleContext } from '../constants/role-details.constants';
import { setAppName, setUserDetails } from '../shared/tenant.util';

@Injectable({
  providedIn: 'root'
})
export class GeneralService {
  private httpClient = inject(HttpClient);
  private authSerice = inject(AuthService);
  selectedSupplierNumbers = new ReplaySubject<number[]>(1);
  private child2ClosedSubject = new BehaviorSubject<boolean>(false);
  private inviteSupplierValue: any;
  private searchRequestParams: any;
  private buyerFeatureList: any;

  getBuyerFeatureDetails() {
    return this.buyerFeatureList;
  }

  setBuyerFeatureDetails(buyerFeatures: any) {
    this.buyerFeatureList = {
      ...buyerFeatures
    };
  }

  getSearchRequestParams() {
    return this.searchRequestParams;
  }

  setSearchRequestParams(request: any) {
    this.searchRequestParams = request;
  }

  getOrgClosed() {
    return this.child2ClosedSubject.asObservable();
  }

  setOrgClosed(closed: boolean) {
    this.child2ClosedSubject.next(closed);
  }

  getInviteSupplierValue() {
    return this.inviteSupplierValue;
  }

  setInviteSupplierValue(toggleValue: boolean) {
    this.inviteSupplierValue = toggleValue;
  }
  
  loadConfig(): Observable<any> {
    return this.httpClient.get(`${ApiUrl.GET_USER_LOGIN_DETAILS}`).pipe(
      tap((userDetails: any) => {
        setUserDetails(userDetails);

        // Store AppName separately for tenant isolation
        // AppName comes from MAG WS-Federation claim via the backend
        if (userDetails.appName) {
          setAppName(userDetails.appName);
          console.log('[GeneralService] AppName stored:', userDetails.appName);
        }
      }),
      // Must return an Observable here. Returning the raw error previously made RxJS
      // throw ("invalid object where a stream was expected"), which rejected the
      // APP_INITIALIZER and aborted app bootstrap entirely. Rethrow using throwError
      // so the APP_INITIALIZER can catch and route to /server-error or /unauthorized.
      catchError((error: any) => {
        const status = error?.status;
        if (status === 0 || status >= 500) {
          // Let the app bootstrap so the interceptor/router can show /server-error.
          return of(null);
        }
        return throwError(() => error);
      })
    );
  }

  getExternalSourceNames(): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.get(`${ApiUrl.GET_EXTERNAL_SOURCE_NAMES}`, { headers }).pipe(
            catchError((error) => {
        return error;
      })
    );

  }

  getSearchData(requestObject: any): Observable<any> {

    return this.httpClient.get(`${ApiUrl.GET_SEARCH_DATA_URL}${requestObject}`).pipe(
            catchError((error) => {
        return error;
      })
    );
  }
  
  getCountryDetails() {
    return this.httpClient.get(`${ApiUrl.GET_COUNTRY_DETAILS_URL}`).pipe(
            catchError((error) => {
        return error;
      })
    );

  }
  getStateDetails(country: string) {
   
    return this.httpClient.get(`${ApiUrl.GET_STATE_BASED_ON_COUNTRY}${country}`).pipe(
            catchError((error) => {
        return error;
      })
    );
  }

  getOrgprofileDetails(requestObject: any) {
    
    return this.httpClient.get(`${ApiUrl.GET_ORGPROFILE_DETAILS_URL}${requestObject}`).pipe(
      catchError((error) => {
        return error;
      })
    );
  }
   
  getHomePageContent() {

    return this.httpClient.get(`${ApiUrl.GET_HOME_PAGE_CONTENT_URL}`).pipe(
      catchError((error) => {
        return error;
      })
    );
  }
  
  getDashboardDetails(requestObject: any): Observable<any> {
    const reqObj = JSON.stringify(requestObject)
    return this.httpClient.get(`${ApiUrl.GET_DASHBOARD_DETAILS}${reqObj}`).pipe(
      catchError((error) => {
        return error;
      })
    );
  }

  getBuyerFeatureList(): Observable<any> {
    /**
     * Request Obj details
     * InviteOrg - 1/0 (0-LMCO 1-Non-LMCO)
     */
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.httpClient.get(`${ApiUrl.GET_BUYER_FEATURE_LIST}`, { ...headers }).pipe(
      tap((featureDetails: any) => {
        this.setBuyerFeatureDetails(featureDetails);
      }),
      catchError((error) => {
        return error;
      })
    );
  }

  
  getSupportFormRequestsList(): Observable<any> {
    return this.httpClient.get(`${ApiUrl.GET_FORM_REQUESTS}`)
    .pipe(
      catchError((error) => {
        return error;
      })
    );
  }

  deleteSupportFormRequestsList(request: any): Observable<any> {
    const headers = new HttpHeaders({ 
      'Content-Type': 'application/json',
    });
    return this.httpClient.post(`${ApiUrl.DELETE_FORM_REQUESTS}`, request, { headers }).pipe(
      catchError((error) => {
        return error;
      })
    );
  }

  getCurrentUserDetails(): Observable<any> {
    return this.httpClient.get(`${ApiUrl.GET_CURRENT_USER_DETAILS}`).pipe(
      catchError((error) => {
        return error;
      })
    );
  }

  getOrgProfile(uniqueId: any): Observable<any> {
    return this.httpClient.get(`${ApiUrl.GET_ORGANIZATION_PROFILE}`+ uniqueId).pipe(
      catchError((error) => {
        return error;
      })
    );
  }
  getLogo(): Observable<Blob> {
    return this.httpClient.get(`${ApiUrl.GET_LOGO}`, { responseType: 'blob' }).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }
}
